import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import Pagination from '../components/Pagination';
import api from '../services/api';

const AttendancePage = () => {
  const [attendance, setAttendance] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', status: '' });

  useEffect(() => {
    fetchAttendance();
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.status) params.append('status', filters.status);

      const response = await api.get(`/attendance/my-attendance?${params}`);
      setAttendance(response.data.data.records);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(1);
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  const calculateHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 'N/A';
    const diff = new Date(checkOut) - new Date(checkIn);
    const hours = Math.floor(diff / 1000 / 60 / 60);
    const minutes = Math.floor((diff / 1000 / 60) % 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <div>
            <h1 className="fs-3 mb-1">My Attendance</h1>
            <p className="mb-0 text-muted">Your check-in and check-out history</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-sm-auto">
                <label className="form-label small mb-1">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  className="form-control"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-sm-auto">
                <label className="form-label small mb-1">End Date</label>
                <input
                  type="date"
                  name="end_date"
                  className="form-control"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-sm-auto">
                <label className="form-label small mb-1">Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Status</option>
                  <option value="checked_in">Checked In</option>
                  <option value="checked_out">Checked Out</option>
                </select>
              </div>

              <div className="col-sm-auto">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setFilters({ start_date: '', end_date: '', status: '' });
                    setPage(1);
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Attendance Table */}
        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table mb-0 text-nowrap table-hover">
                <thead className="table-light border-light">
                  <tr>
                    <th>Date</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Tasks Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        No attendance records found
                      </td>
                    </tr>
                  ) : (
                    attendance.map((record) => (
                      <tr key={record._id} className="align-middle">
                        <td>{formatDate(record.date)}</td>
                        <td>{formatTime(record.check_in)}</td>
                        <td>{formatTime(record.check_out)}</td>
                        <td>{calculateHours(record.check_in, record.check_out)}</td>
                        <td>
                          <span className={`badge ${record.status === 'checked_out' ? 'bg-success' : 'bg-primary'}`}>
                            {record.status === 'checked_out' ? 'Checked Out' : 'Checked In'}
                          </span>
                        </td>
                        <td className="small" style={{ maxWidth: '300px' }}>
                          {record.tasks_completed || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination pagination={pagination} onPageChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AttendancePage;
